import React, { useEffect, useMemo, useState } from "react"
import { shallowEqual, useSelector } from "react-redux"
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../../_metronic/_helpers";
import { NavLink, Redirect } from "react-router-dom";
import { Permissions, PermissionType } from "../../../../../util/Permissions";
import { formatStatus, permissionCssClass, ucFirst } from "../../../../../util/Helpers";
import SimpleTable from "my-simple-react-table";
import { useGetThemeAssetsQuery, useGetThemesQuery } from "../../../../../../redux/apiSlice";
import TableSkeleton from "../../../../../util/TableSkeleton";
import DeleteThemeModal from "../../modals/DeleteThemeModal";
import { useSubheader } from "../../../../../../_metronic/layout";
import Skeleton from "react-loading-skeleton";
import EditThemeImagesModal from "../../modals/EditThemeImagesModal";

export const ViewThemeDetails = ({ match }) => {
    const adminUser = useSelector(({ auth }) => (auth.user), shallowEqual);
    const { PUBLIC_URL } = process.env;
    const subheader = useSubheader();

    const { isFetching: themesFetching, data: themes } = useGetThemesQuery(match?.params?.userGroup)

    const themeDetails = useMemo(() => {
        return themes?.find((theme) => theme.id === match.params.themeId)
    }, [themes, match.params.themeId])

    useEffect(() => {
        subheader.setTitle(themeDetails?.name || <div style={{ width: "100px" }}><Skeleton count="1" /></div>)
    }, [subheader, themeDetails])

    const { isFetching: themeAssetsFetching, data: themeAssets} = useGetThemeAssetsQuery(match?.params?.themeId)

    /* Theme Modal State Management */
    const [themeModal, setThemeModal] = useState({
        showDeleteThemeModal: false,
        showEditThemeImagesModal: false
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setThemeModal({ ...themeModal, [modal]: !themeModal[modal] })
    }

    /* Delete Theme Modal */
    const showDeleteThemeModal = () => {
        setThemeModal({ ...themeModal, showDeleteThemeModal: true });
    }

    /* Edit Theme Images Modal */
    const showEditThemeImagesModal = () => {
        setThemeModal({ ...themeModal, showEditThemeImagesModal: true });
    }

    const formatAsset = row => {
        return <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
            <div>
                <a href={`${PUBLIC_URL}/instruments/${row?.id}`} rel="noopener noreferrer" target="_blank"
                    className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {row.symbol}
                </a>
            </div>
        </div>
    }

    return (
        (!themesFetching && !themeDetails)
            ? <Redirect to={`/themes/view/${match?.params?.userGroup}`} />
            : <div>
                <div className="card col card-custom mb-6 mr-lg-6">
                    {/* <!--begin::Header--> */}
                    <div className="card-header border-0">
                        <h3 className="card-title font-weight-bolder text-dark">Basic Details</h3>
                        {
                            themeDetails && <div
                                className={`card-toolbar ${permissionCssClass(adminUser, Permissions.THEMES, PermissionType.UPDATE)}`}>
                                <div className="dropdown dropdown-inline">
                                    <NavLink to={`/themes/edit/${match.params.userGroup}/${match.params.themeId}?tab=1`}
                                        title="Edit Basic Details"
                                        className="btn btn-icon btn-default border btn-bg-white btn-hover-light-primary btn-sm mr-n4"

                                    >
                                        <span className="svg-icon svg-icon-md svg-icon-primary">
                                            <SVG
                                                title=" "
                                                src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                                            />
                                        </span>
                                    </NavLink>
                                </div>
                            </div>
                        }
                    </div>
                    {/* <!--end::Header--> */}
                    {/* <!--begin::Body--> */}
                    <div className="card-body pt-2">
                        {/* <!--begin::Item--> */}
                        <div className="row">
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Name
                                </div>
                                <div
                                    className="text-muted font-weight-bold font-size-sm my-1"
                                >
                                    {themeDetails?.name}
                                </div>
                                {!themeDetails && <Skeleton height={"1rem"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div
                                    className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Status
                                </div>
                                <div
                                    className="text-muted font-weight-bold font-size-sm my-1"> {formatStatus(themeDetails?.status)}
                                </div>
                                {!themeDetails && <Skeleton height={"1rem"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">User
                                    Group
                                </div>
                                <div
                                    className="text-muted font-weight-bold font-size-sm my-1"> {ucFirst(themeDetails?.userGroup)}
                                </div>
                                {!themeDetails && <Skeleton height={"1rem"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            <div className="w-100 h-6" />
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div
                                    className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Description
                                </div>
                                <div
                                    className="text-muted font-weight-bold font-size-sm my-1"> {ucFirst(themeDetails?.description)}
                                </div>
                                {!themeDetails && <Skeleton height={"1rem"} width="100%" />}
                            </div>

                        </div>
                        {/* <!--end::Item--> */}

                    </div>
                    {/* <!--end::Body--> */}
                </div>
                <div className="card col card-custom mb-6">
                    {/* <!--begin::Header--> */}
                    <div className="card-header border-0">
                        <h3 className="card-title font-weight-bolder text-dark">Images</h3>
                        <div
                            className={`card-toolbar ${permissionCssClass(adminUser, Permissions.THEMES, PermissionType.UPDATE)}`}>
                            {
                                themeDetails && <div className="dropdown dropdown-inline">
                                    <div
                                        onClick={showEditThemeImagesModal}
                                        title="Edit Basic Details"
                                        className="btn btn-icon btn-default border btn-bg-white btn-hover-light-primary btn-sm mr-n4"
                                    >
                                        <span className="svg-icon svg-icon-md svg-icon-primary">
                                            <SVG
                                                title=" "
                                                src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                                            />
                                        </span>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    {/* <!--end::Header--> */}
                    {/* <!--begin::Body--> */}
                    <div className="card-body pt-2">
                        {/* <!--begin::Item--> */}
                        <div className="row">
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Logo
                                </div>
                                {themeDetails?.name && !themeDetails?.meta.logo
                                    ? <div
                                        className="text-muted font-weight-bold font-size-sm my-1"
                                    >
                                        None
                                    </div>
                                    : <img src={themeDetails?.meta.logo} className="img-fluid" alt={themeDetails?.name} />
                                }
                                {!themeDetails && <Skeleton height={"186px"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div
                                    className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Banner
                                </div>
                                {themeDetails?.name && !themeDetails?.meta.banner
                                    ? <div
                                        className="text-muted font-weight-bold font-size-sm my-1"
                                    >
                                        None
                                    </div>
                                    : <img src={themeDetails?.meta.banner} className="img-fluid"
                                        alt={themeDetails?.name} />
                                }
                                {!themeDetails && <Skeleton height={"186px"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div
                                    className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Spotlight (Web)
                                </div>
                                {themeDetails?.name && !themeDetails?.meta.webSpotlightImage
                                    ? <div
                                        className="text-muted font-weight-bold font-size-sm my-1"
                                    >
                                        None
                                    </div>
                                    : <img src={themeDetails?.meta.webSpotlightImage} className="img-fluid"
                                        alt={themeDetails?.name} />
                                }
                                {!themeDetails && <Skeleton height={"186px"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                            {/* <!--begin::Field--> */}
                            <div className="col mt-0 mb-2 pr-3">
                                <div
                                    className="text-dark-75 font-weight-bolder font-size-lg text-hover-primary">Spotlight (Mobile)
                                </div>
                                {themeDetails?.name && !themeDetails?.meta.mobileSpotlightImage
                                    ? <div
                                        className="text-muted font-weight-bold font-size-sm my-1"
                                    >
                                        None
                                    </div>
                                    : <img src={themeDetails?.meta.mobileSpotlightImage} className="img-fluid"
                                        alt={themeDetails?.name} />
                                }
                                {!themeDetails && <Skeleton height={"186px"} width="100%" />}
                            </div>
                            {/* <!--end::Field--> */}
                        </div>
                        {/* <!--end::Item--> */}

                    </div>
                    {/* <!--end::Body--> */}
                </div>
                <div className="row">
                    <div className="card col card-custom mb-6">
                        {/* <!--begin::Header--> */}
                        <div className="card-header border-0">
                            <h3 className="card-title font-weight-bolder text-dark">Instruments</h3>
                            {
                                themeDetails && <div
                                    className={`card-toolbar ${permissionCssClass(adminUser, Permissions.THEMES, PermissionType.UPDATE)}`}>
                                    <div className="dropdown dropdown-inline">
                                        <NavLink to={`/themes/edit/${match.params.userGroup}/${match.params.themeId}?tab=1`}
                                            title="Edit Basic Details"
                                            className="btn btn-icon btn-default border btn-bg-white btn-hover-light-primary btn-sm mr-n4"

                                        >
                                            <span className="svg-icon svg-icon-md svg-icon-primary">
                                                <SVG
                                                    title=" "
                                                    src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                                                />
                                            </span>
                                        </NavLink>
                                    </div>
                                </div>
                            }
                        </div>
                        {/* <!--end::Header--> */}
                        <div className="card-body pt-0">
                            {
                                themeAssetsFetching
                                    ? <TableSkeleton />
                                    : <SimpleTable
                                        data={themeAssets || []}
                                        columns={[
                                            { title: "ASSET", format: formatAsset },
                                            { title: "COUNTRY", key: "country" },
                                            { title: "EXCHANGE", key: "exchange" },
                                            { title: "CURRENCY", key: "currency" },
                                            { title: "STATUS", format: row => formatStatus(row.status) },
                                        ]}
                                    />
                            }
                        </div>
                    </div>
                </div>
                {
                    themeDetails && <div className={"row justify-content-end"}>
                        <button
                            type="button"
                            className={`btn btn-danger font-weight-bold px-9 py-4 ${permissionCssClass(adminUser, Permissions.THEMES, PermissionType.DELETE)}`}
                            onClick={showDeleteThemeModal}
                        >
                            Delete Theme
                        </button>
                    </div>
                }

                {
                    themeDetails && <DeleteThemeModal
                        {...themeModal}
                        onHide={toggleModal("showDeleteThemeModal")}
                        details={themeDetails}
                    />
                }

                {
                    themeDetails && <EditThemeImagesModal
                        {...themeModal}
                        onHide={toggleModal("showEditThemeImagesModal")}
                        userGroup={match.params.userGroup}
                        themeDetails={themeDetails}
                    />
                }
            </div>
    )
}